<template>
  <ModuleWrapper
    :moduleId="moduleId"
    :image="imageSrcPath('assets/branding/logo.svg')"
    title="Video Stream Background"
    class="tw-relative"
    @initializeData="initializeData"
  >
    <template #main>
      <div>
        <StudioSelect
          id="desktop-background-type"
          label="Desktop Background Type"
          :options="[
            { value: 'Image', label: 'Image' },
            { value: 'Solid Color', label: 'Solid Color' },
            { value: 'Gradient', label: 'Gradient' }
          ]"
          size="small"
          orientation="horizontal"
          class="tw-mb-4"
          :value="selectedDesktopBackgroundType"
          @change="onSelectDesktopBackgroundType"
        />
        <ImageUploadButton
          v-if="selectedDesktopBackgroundType === 'Image'"
          label="Min of 1024x768, Max of 2400x1800"
          :image="desktopBackgroundImage"
          :acceptedTypes="['.ico', '.png', '.jpg', '.jpeg', '.webp']"
          :s3BucketPath="s3BucketPath"
          :imageValidation="backgroundImageValidationDesktop"
          required
          class="tw-mb-4"
          @changeImageUrl="onDesktopBackgroundUrlChange"
        />
        <ConditionalColorPicker
          v-else-if="selectedDesktopBackgroundType === 'Solid Color'"
          id="desktop-background-color-picker"
          label="Desktop Background Color"
          type="color"
          class="tw-mb-4"
          :initialColor="desktopColor.style"
          @colorChange="onDesktopColorChange"
        />
        <StudioColorPickerInput
          v-else-if="selectedDesktopBackgroundType === 'Gradient'"
          id="desktop-background-gradient-picker"
          label="Desktop Background Gradient"
          type="gradient"
          class="tw-mb-4"
          :initialGradient="desktopGradient"
          @gradientChange="onDesktopGradientChange"
        />
      </div>

      <div>
        <StudioSelect
          id="mobile-background-type"
          label="Mobile Background Type"
          :options="[
            { value: 'Image', label: 'Image' },
            { value: 'Solid Color', label: 'Solid Color' },
            { value: 'Gradient', label: 'Gradient' }
          ]"
          size="small"
          orientation="horizontal"
          class="tw-mb-4"
          :value="selectedMobileBackgroundType"
          @change="onSelectMobileBackgroundType"
        />
        <ImageUploadButton
          v-if="selectedMobileBackgroundType === 'Image'"
          label="Min of 600x800, Max of 1200x1600."
          :image="mobileBackgroundImage"
          :acceptedTypes="['.ico', '.png', '.jpg', '.jpeg', '.webp']"
          :s3BucketPath="s3BucketPath"
          :imageValidation="backgroundImageValidationMobile"
          required
          @changeImageUrl="onMobileBackgroundUrlChange"
        />
        <ConditionalColorPicker
          v-else-if="selectedMobileBackgroundType === 'Solid Color'"
          id="mobile-background-color-picker"
          label="Mobile Background Color"
          type="color"
          :initialColor="mobileColor.style"
          @colorChange="onMobileColorChange"
        />
        <StudioColorPickerInput
          v-else-if="selectedMobileBackgroundType === 'Gradient'"
          id="desktop-background-gradient-picker"
          label="Mobile Background Gradient"
          type="gradient"
          :initialGradient="mobileGradient"
          @gradientChange="onMobileGradientChange"
        />
      </div>
    </template>
  </ModuleWrapper>
</template>

<script>
import { mapMutations, mapGetters, mapState } from 'vuex';

import ModuleWrapper from './ModuleWrapper.vue';
import ImageUploadButton from '../components/ImageUploadButton.vue';
import { color, gradient } from '../data/defaultValues';

import { GET_RESOURCES_PATH, BACKGROUND_IMG_VALIDATION_DESKTOP, BACKGROUND_IMG_VALIDATION_MOBILE } from '@/constants';
import ConditionalColorPicker from '@/studio/lookAndFeel/components/ConditionalColorPicker.vue';
import StudioColorPickerInput from '@/studioComponents/StudioColorPickerInput.vue';
import StudioSelect from '@/studioComponents/StudioSelect.vue';
import {
  destructureRgbColor,
  isColorString,
  destructureGradientColor,
  isUrlString,
  isGradientString,
  getRGBAstring,
  destructureUrl
} from '@/utils/utils.js';

const cloneDeep = require('lodash.clonedeep');

export default {
  name: 'VideoStreamBackground',
  components: {
    ModuleWrapper,
    StudioSelect,
    ConditionalColorPicker,
    StudioColorPickerInput,
    ImageUploadButton
  },
  props: {
    moduleId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      selectedDesktopBackgroundType: 'Image',
      selectedMobileBackgroundType: 'Image',
      s3BucketPath: `${GET_RESOURCES_PATH(this.$route.params.eventId)}`,
      desktopColor: color,
      mobileColor: color,
      desktopGradient: cloneDeep(gradient),
      mobileGradient: cloneDeep(gradient),
      backgroundImageValidationDesktop: BACKGROUND_IMG_VALIDATION_DESKTOP,
      backgroundImageValidationMobile: BACKGROUND_IMG_VALIDATION_MOBILE
    };
  },
  mounted() {
    this.initializeData();
  },
  computed: {
    ...mapGetters('branding', {
      getInitialModuleData: 'getInitialModuleData'
    }),
    ...mapState('branding', ['isCurrentModuleInValid', 'changesMadeInThemeObj']),
    isFormValid() {
      if (this.selectedDesktopBackgroundType === 'Image' && !this.desktopBackgroundImage) {
        return false;
      }
      if (this.selectedMobileBackgroundType === 'Image' && !this.mobileBackgroundImage) {
        return false;
      }

      return true;
    },
    desktopBackgroundImage() {
      const moduleKeys = ['stage_background'];
      const { stage_background } = this.getInitialModuleData(moduleKeys);
      return isUrlString(stage_background) ? destructureUrl(stage_background) : '';
    },
    mobileBackgroundImage() {
      const moduleKeys = ['mobile_stage_background'];
      const { mobile_stage_background } = this.getInitialModuleData(moduleKeys);
      return isUrlString(mobile_stage_background) ? destructureUrl(mobile_stage_background) : '';
    }
  },
  watch: {
    isFormValid(newInputs) {
      this.setCurrentModuleInvalid(!newInputs);
    }
  },
  methods: {
    ...mapMutations('branding', {
      setChangesMadeInThemeObj: 'setChangesMadeInThemeObj',
      setCurrentModuleInvalid: 'setCurrentModuleInvalid'
    }),
    initializeData() {
      const moduleKeys = ['stage_background', 'mobile_stage_background'];
      const { stage_background: stageBackground, mobile_stage_background: mobileStageBackground } =
        this.getInitialModuleData(moduleKeys);

      const isDesktopGradient = isGradientString(stageBackground);
      const isDesktopSolidColor = isColorString(stageBackground);
      const isMobileGradient = isGradientString(mobileStageBackground);
      const isMobileSolidColor = isColorString(mobileStageBackground);

      if (isDesktopGradient) {
        this.desktopGradient = destructureGradientColor(stageBackground);
        this.selectedDesktopBackgroundType = 'Gradient';
        this.desktopColor = color;
      } else if (isDesktopSolidColor) {
        this.desktopColor = destructureRgbColor(stageBackground);
        this.selectedDesktopBackgroundType = 'Solid Color';
        this.desktopGradient = cloneDeep(gradient);
      } else {
        this.selectedDesktopBackgroundType = 'Image';
        this.desktopColor = color;
        this.desktopGradient = cloneDeep(gradient);
      }

      if (isMobileGradient) {
        this.mobileGradient = destructureGradientColor(mobileStageBackground);
        this.selectedMobileBackgroundType = 'Gradient';
        this.mobileColor = color;
      } else if (isMobileSolidColor) {
        this.mobileColor = destructureRgbColor(mobileStageBackground);
        this.selectedMobileBackgroundType = 'Solid Color';
        this.mobileGradient = cloneDeep(gradient);
      } else {
        this.selectedMobileBackgroundType = 'Image';
        this.mobileColor = color;
        this.mobileGradient = cloneDeep(gradient);
      }
    },
    onSelectDesktopBackgroundType(e) {
      const selectedType = e.target.value;

      if (selectedType === 'Gradient') {
        this.setChangesMadeInThemeObj({
          stage_background: this.desktopGradient.style
        });
      } else if (selectedType === 'Solid Color') {
        this.setChangesMadeInThemeObj({
          stage_background: this.desktopColor.style
        });
      }

      this.selectedDesktopBackgroundType = selectedType;
    },
    onSelectMobileBackgroundType(e) {
      const selectedType = e.target.value;

      if (selectedType === 'Gradient') {
        this.setChangesMadeInThemeObj({
          mobile_stage_background: this.mobileGradient.style
        });
      } else if (selectedType === 'Solid Color') {
        this.setChangesMadeInThemeObj({
          mobile_stage_background: this.mobileColor.style
        });
      }

      this.selectedMobileBackgroundType = selectedType;
    },
    onDesktopColorChange(color) {
      if (color.alpha > 1) {
        color.alpha = 1;
        color.style = getRGBAstring(color);
      }

      this.desktopColor = color;
      this.setChangesMadeInThemeObj({
        stage_background: color.style
      });
    },
    onMobileColorChange(color) {
      if (color.alpha > 1) {
        color.alpha = 1;
        color.style = getRGBAstring(color);
      }

      this.mobileColor = color;
      this.setChangesMadeInThemeObj({
        mobile_stage_background: color.style
      });
    },
    onDesktopGradientChange(gradient) {
      this.desktopGradient = gradient;
      this.setChangesMadeInThemeObj({
        stage_background: gradient.style
      });
    },
    onMobileGradientChange(gradient) {
      this.mobileGradient = gradient;
      this.setChangesMadeInThemeObj({
        mobile_stage_background: gradient.style
      });
    },
    onDesktopBackgroundUrlChange(url) {
      this.setChangesMadeInThemeObj({
        stage_background: `Url('${url}')`
      });
    },
    onMobileBackgroundUrlChange(url) {
      this.setChangesMadeInThemeObj({
        mobile_stage_background: `Url('${url}')`
      });
    }
  }
};
</script>
